/**
 * ModalBase Component
 */

import './styles.scss';

import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { VelocityComponent } from 'velocity-react';

import Button, { ButtonProps } from '../../../atoms/buttons/Button';
import Heading, { HeadingProps } from '../../../atoms/text/Heading';

export type OnCloseModalHandler = (actionId: string) => void;

enum Position {
  Center = 'center',
  Top = 'top',
}

interface IModalExposedProps {
  actions: React.ReactFragment;
  className?: string;
  onClose?: OnCloseModalHandler;
  position?: Position;
}

interface IModalPartProps extends Pick<IModalExposedProps, 'position'> {
  visible: boolean;
}

interface IModalState {
  visible: boolean;
}

type IModalDefaultProps = DefaultProps<IModalExposedProps, 'position'>;
type IModalProps = IModalExposedProps & IModalDefaultProps;

const classes = new BEM('modal');

const backgroundAnimationProps = {
  animation: ['transition.fadeIn', 'transition.fadeOut'],
  duration: 350,
  delay: 750,
};

const wrapperAnimationProps = {
  animation: {
    [Position.Center]: ['transition.expandIn', 'transition.shrinkOut'],
    [Position.Top]: ['transition.slideDownBigIn', 'transition.slideUpBigOut'],
  },
  duration: 350,
  delay: 350,
};

const defaultProps: IModalDefaultProps = {
  position: Position.Center,
};

class ModalBase extends React.Component<IModalProps, IModalState> {
  static defaultProps = defaultProps;

  transitionOutTimer: number;
  state = {
    visible: true,
  };

  onClickAction: React.MouseEventHandler<HTMLDivElement> = ({ target }) => {
    const htmlTarget = target as HTMLElement;
    const button = htmlTarget.closest('.modal__action');

    this.setState({ visible: false }, () => {
      this.transitionOutTimer = window.setTimeout(() => {
        if (button && this.props.onClose) {
          this.props.onClose(button.id);
        }
      }, 2000);
    });
  }

  render() {
    return (
      <div {...classes('', { [this.props.position]: true }, this.props.className)}>
        <Background visible={this.state.visible} />
        <Wrapper visible={this.state.visible} position={this.props.position}>
          {this.props.children}
          <div onClick={this.onClickAction} {...classes('actions')}>
            {this.props.actions}
          </div>
        </Wrapper>
      </div>
    );
  }
}

const Component = ModalBase as React.ComponentType<IModalExposedProps>;

// Modal Background
const Background: React.SFC<IModalPartProps> = ({ visible }) => {
  const [In, Out] = backgroundAnimationProps.animation;
  return (
    <VelocityComponent
      animation={visible ? In : Out}
      delay={visible ? 0 : backgroundAnimationProps.delay}
      duration={backgroundAnimationProps.duration}
      runOnMount
    >
      <div {...classes('background')} />
    </VelocityComponent>
  );
};

// Modal Content Wrapper
const Wrapper: React.SFC<IModalPartProps> = ({ children, position, visible }) => {
  const [In, Out] = wrapperAnimationProps.animation[position!];
  return (
    <VelocityComponent
      animation={visible ? In : Out}
      delay={visible ? wrapperAnimationProps.delay : 0}
      duration={wrapperAnimationProps.duration}
      runOnMount
    >
      <div {...classes('wrapper')}>{children}</div>
    </VelocityComponent>
  );
};

// Modal Content
const Content: React.SFC<React.HTMLAttributes<HTMLDivElement>> = ({ children, className, ...props }) => (
  <div {...classes('content', '', className) } {...props}>{children}</div>
);

// Modal Title
const Title: React.SFC<Optional<HeadingProps>> = ({ children, className, is, ...headingProps }) => (
  <Heading is={is || 'h4'} {...classes('title', '', className) } {...headingProps}>{children}</Heading>
);

// Modal Action
type ModalActionStyle = 'cancel' | 'destructive' | 'default';

interface IModalActionProps {
  Element: React.ReactElement<ButtonProps>;
  id: string;
  style?: ModalActionStyle;
}

type IModalStyledActionProps = Pick<ButtonProps, 'id' | 'label' | 'onClick' | 'style'>;

function getModalActionStyleProps(style?: ModalActionStyle): Partial<ButtonProps> {
  switch (style) {
    case 'cancel':
      return {
        textColor: 'bw7',
      };
    case 'destructive':
      return {
        textColor: 'dangerous',
      };
    default:
      return {
        textColor: 'accent',
      };
  }
}

const Action: React.SFC<IModalActionProps> = ({ Element, id, style }) =>
  React.cloneElement(Element, {
    ...Element.props,
    icon: null,
    colorScheme: 'transparent',
    size: 'xxlarge',
    ...getModalActionStyleProps(style),
    ...classes('action'),
    id,
  });

const CancelAction: React.SFC<IModalStyledActionProps> = (props) => (
  <Action
    Element={<Button {...props} />}
    id={props.id || 'cancel'}
    style="cancel"
  />
);

const DefaultAction: React.SFC<IModalStyledActionProps> = (props) => (
  <Action
    Element={<Button {...props} />}
    id={props.id || 'default'}
    style="default"
  />
);

const DestructiveAction: React.SFC<IModalStyledActionProps> = (props) => (
  <Action
    Element={<Button {...props} />}
    id={props.id || 'destructive'}
    style="destructive"
  />
);

export {
  Action,
  Background,
  CancelAction,
  Component,
  Content,
  DefaultAction,
  DestructiveAction,
  Position,
  Title,
  Wrapper,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/modals/ModalBase/index.tsx